import { Stack } from '@mui/material';
import { Assessment, FilterKeys, SavedFilter } from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { ExpandableSearch } from '@schooly/style';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { MarkbookFilterPreview } from '../../../../context/markbook/MarkbookFilterContext';
import { MARKBOOK_FOR_GROUPS_STUDENTS_COL_WIDTH } from '../common/assessmentStyling';
import {
  AverageResults,
  AverageResultsColumnData,
  AverageResultsWrapper,
} from '../common/AverageResults';
import {
  createGradeFinder,
  getAssessmentEntryColorFactory,
  getAverageColorFactory,
} from '../common/colouredCells';
import { useBaselineAssessmentConfig } from '../common/useBaselineAssessmentsConfig';
import { getBaseline, getMarkbookSubjects, MARKBOOK_TUTOR_GROUP } from '../common/utils';
import { MarkbookHeader } from '../MarkbookHeader';
import {
  DEFAULT_MARKBOOK_VIEW_SETTINGS,
  MarkbookOverrideValueAndDisable,
  MarkbookSettings,
  MarkbookSettingsLoading,
  MarkbookViewSettings,
} from '../MarkbookSettings/MarkbookSettings';
import { shouldDisableScoreInPercentage } from '../MarkbookSettings/utils';
import { MarkbookPreviewExport } from './MarkbookPreviewExport';
import { MarkbookPreviewSkeletonTable } from './MarkbookPreviewSkeletonTable';
import { MarkbookPreviewTable } from './MarkbookPreviewTable';
import { useAssessmentsMarkbookForGroups } from './useAssessmentsMarkbookForGroups';

export const MARKBOOK_SETTINGS_STORAGE_KEY = 'markbook-settings-v2';

type MarkbookPreviewContentProps = {
  filter?: SavedFilter;
  initialSettings: MarkbookViewSettings | null;
  filterPreview: MarkbookFilterPreview | null;
  onMarkbookEdit: () => void;
  onMarkbookCreate: () => void;
  canShowFilter: boolean;
} & PropsWithChildren;

export const MarkbookPreviewContent: FC<MarkbookPreviewContentProps> = ({
  filter,
  filterPreview,
  onMarkbookEdit,
  onMarkbookCreate,
  canShowFilter,
  children,
  initialSettings,
}) => {
  const { formatMessage } = useIntl();
  const { showNotification } = useNotifications();

  const [settings, setSettings] = useState<MarkbookViewSettings>(
    initialSettings ?? DEFAULT_MARKBOOK_VIEW_SETTINGS,
  );

  useEffect(() => {
    localStorage.setItem(MARKBOOK_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const {
    assessments,
    lists,
    isLoading,
    isSorting,
    markbookQueriesParams,
    handleChangeQuery,
    handleChangeSort,
    markbookParams,
    averageData,
    subjectAverageData,
    isAverageLoading,
    isListsLoading,
    handleSuccessfulEntryUpdate,
    setBenchmarkEnabled,
    setBenchmarkAverage,
    setBenchmarkSubjectAverage,
    benchmarkEnabled,
  } = useAssessmentsMarkbookForGroups({ filterPreview, filter, settings });

  const { baselineAssessmentConfig, setBaselineAssessmentConfig } = useBaselineAssessmentConfig({
    settings,
    setBenchmarkEnabled,
    benchmarkEnabled,
  });

  const overrideSettings: MarkbookOverrideValueAndDisable = useMemo(() => {
    return {
      scoreInPercentage: shouldDisableScoreInPercentage(assessments) ? false : undefined,
      coloredCells: baselineAssessmentConfig ? true : undefined,
    };
  }, [assessments, baselineAssessmentConfig]);

  const avgResultEnabled = overrideSettings.avgResults ?? settings.avgResults;
  const coloredCellsEnabled = overrideSettings.coloredCells ?? settings.coloredCells;
  const scoreInPercentageEnabled = overrideSettings.scoreInPercentage ?? settings.scoreInPercentage;

  const gradeFinders = useMemo(() => {
    const finders = new Map<string, ReturnType<typeof createGradeFinder>>();

    if (!avgResultEnabled && !coloredCellsEnabled) return finders;

    Object.entries(lists ?? {}).forEach(([selectListId, grade]) => {
      finders.set(selectListId, createGradeFinder(grade.options));
    });

    return finders;
  }, [lists, avgResultEnabled, coloredCellsEnabled]);

  const getAssessmentEntryColor = coloredCellsEnabled
    ? getAssessmentEntryColorFactory(averageData, lists, gradeFinders)
    : undefined;

  const getAverageCellColor =
    coloredCellsEnabled && !baselineAssessmentConfig
      ? getAverageColorFactory(subjectAverageData)
      : undefined;

  const onLockedCellClick = useCallback(() => {
    showNotification({
      message: formatMessage({ id: 'assessments-markbook-EditingLocked' }),
      actions: [
        {
          textId: 'assessments-markbook-UnlockEditing',
          handler: () => {
            setSettings((s) => ({ ...s, editable: true }));
          },
        },
      ],
    });
  }, [formatMessage, showNotification]);

  const renderAverageResults = (renderedAssessments: Assessment[]) => {
    let columns: AverageResultsColumnData[] = [];
    let fixedColumn: AverageResultsColumnData | undefined;

    switch (settings.columnArrangeBy) {
      case FilterKeys.Subject: {
        const baseline = getBaseline(renderedAssessments, baselineAssessmentConfig);

        switch (baseline?.kind) {
          case 'assessment':
            fixedColumn = {
              type: 'assessment',
              assessment: baseline.assessment,
              subject: baseline.subject,
              methodId: baseline.method.id ?? '',
            };
            break;
          case 'average':
            {
              const subjectId =
                baseline.subject.id === MARKBOOK_TUTOR_GROUP.id ? null : baseline.subject.id;
              const average = subjectAverageData.find((a) => a.subject_id === subjectId);

              fixedColumn = {
                type: 'average',
                subject: baseline.subject,
                average: average?.average_score ?? null,
              };
            }
            break;
        }

        const subjects = getMarkbookSubjects(
          renderedAssessments.flatMap((assessment) => assessment.groups),
        );
        for (const subject of subjects) {
          const subjectGroupsIds = subject.groups.map(({ id }) => id);
          const subjectAssessments = renderedAssessments.filter((assessment) =>
            assessment.groups.some(({ id }) => subjectGroupsIds.includes(id)),
          );
          for (const assessment of subjectAssessments) {
            for (const method of assessment.methods) {
              columns.push({
                type: 'assessment',
                assessment,
                subject,
                methodId: method.id ?? '',
              });
            }
          }
          const subjectId = subject.id === MARKBOOK_TUTOR_GROUP.id ? null : subject.id;
          columns.push({
            type: 'average',
            subject,
            average:
              subjectAverageData.find((a) => a.subject_id === subjectId)?.average_score ?? null,
          });
        }
        break;
      }
      case FilterKeys.Assessment:
        columns = renderedAssessments.flatMap((assessment) => {
          const subjects = getMarkbookSubjects(assessment.groups);
          return subjects.flatMap((subject) =>
            assessment.methods.map((method) => ({
              type: 'assessment',
              assessment,
              subject,
              methodId: method.id ?? '',
            })),
          );
        });
        break;

      default: {
        const _: never = settings.columnArrangeBy;
        return _;
      }
    }

    return (
      <AverageResultsWrapper opened={avgResultEnabled}>
        <AverageResults
          fixedColumn={fixedColumn}
          columns={columns}
          averageData={averageData}
          subjectAverageData={subjectAverageData}
          studentColumnWidth={MARKBOOK_FOR_GROUPS_STUDENTS_COL_WIDTH}
          isLoading={isAverageLoading || isListsLoading}
          gradeFinders={gradeFinders}
          setBenchmarkEnabled={setBenchmarkEnabled}
          setBenchmarkAverage={setBenchmarkAverage}
          setBenchmarkSubjectAverage={setBenchmarkSubjectAverage}
          benchmarkEnabled={benchmarkEnabled}
          gradeList={lists}
          showPercentage={scoreInPercentageEnabled}
        />
      </AverageResultsWrapper>
    );
  };

  return (
    <Stack height="100%" gap={1}>
      <MarkbookHeader
        canShowFilter={canShowFilter}
        buttonTextId="assessments-markbook-createMarkbook"
        onButtonClick={onMarkbookCreate}
      >
        <ExpandableSearch
          value={markbookParams.query ?? ''}
          onChange_MemoizedCallbackOnly={handleChangeQuery}
          placeholder={formatMessage({ id: 'people-Search' })}
          iconProps={{ sx: { color: 'primary.main' }, inverse: false }}
          withDebounce
        />
        <MarkbookPreviewExport params={markbookParams} />
      </MarkbookHeader>
      {isLoading ? (
        <MarkbookSettingsLoading />
      ) : (
        <MarkbookSettings
          baselineEnabled={!!baselineAssessmentConfig}
          settings={settings}
          onSetSettings={setSettings}
          overrideValueAndDisable={overrideSettings}
        />
      )}
      <Stack sx={{ flex: '1 1 0', minHeight: 0, mb: -2.5 }}>
        {isLoading ? (
          <MarkbookPreviewSkeletonTable />
        ) : (
          <MarkbookPreviewTable
            //key is used to remount the component when the queriesParams length change
            //to avoid error in useGetMarkbookQueries
            key={`markbook-preview-table-${markbookQueriesParams.length}`}
            columnArrangeBy={settings.columnArrangeBy}
            queriesParams={markbookQueriesParams}
            assessments={assessments}
            lists={lists}
            onEditMarkbook={onMarkbookEdit}
            onChangeSort={handleChangeSort}
            isSorting={isSorting}
            sort={markbookParams.sort?.[0]}
            showPercentage={scoreInPercentageEnabled}
            onSuccessfulEntryUpdate={handleSuccessfulEntryUpdate}
            getAssessmentEntryColor={getAssessmentEntryColor}
            getAverageCellColor={getAverageCellColor}
            onCellClickOverride={!settings.editable ? onLockedCellClick : undefined}
            baselineConfig={baselineAssessmentConfig}
            onSetBaselineConfig={setBaselineAssessmentConfig}
            showStudentsAverage={avgResultEnabled}
          >
            {renderAverageResults}
          </MarkbookPreviewTable>
        )}

        {children}
      </Stack>
    </Stack>
  );
};
